/**
 * Where an account lands when nothing asked for a particular page.
 *
 * One account can be a seller, a buyer and an affiliate at once. Each login
 * page used to assume one of them: the seller login sent everyone to the
 * dashboard, the buyer login sent everyone to /account. A buyer who later
 * opened a store kept landing on /account, behind a dashboard button that on a
 * phone was an unlabelled grey icon, and an affiliate who never bought was
 * sent to "become a seller". Now an account with one side goes straight there,
 * and an account with several is asked.
 */

export type AccountRole = "seller" | "buyer" | "affiliate";

export interface RoleCaps {
  isSeller: boolean;
  hasPurchased: boolean;
  isAffiliate: boolean;
}

export const ROLE_HOME: Record<AccountRole, string> = {
  seller: "/dashboard/overview",
  buyer: "/account",
  affiliate: "/affiliate/overview",
};

export const CHOOSER_PATH = "/masuk";

/** The sides this account can open, in the order the chooser shows them. */
export function rolesOf(caps: RoleCaps): AccountRole[] {
  const roles: AccountRole[] = [];
  if (caps.isSeller) roles.push("seller");
  if (caps.hasPurchased) roles.push("buyer");
  if (caps.isAffiliate) roles.push("affiliate");
  return roles;
}

/**
 * No side at all is someone who signed up and never bought, sold or promoted:
 * the only useful place left is the seller signup.
 */
export function homeFor(caps: RoleCaps): string {
  const roles = rolesOf(caps);
  if (roles.length === 0) return "/register";
  if (roles.length === 1) return ROLE_HOME[roles[0]];
  return CHOOSER_PATH;
}

/** The side a path belongs to, so a switcher can leave out the one already open. */
export function roleOfPath(pathname: string): AccountRole | null {
  if (pathname.startsWith("/dashboard")) return "seller";
  if (pathname.startsWith("/account")) return "buyer";
  if (pathname.startsWith("/affiliate")) return "affiliate";
  return null;
}
